import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Select from "react-select";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";

require("./assets/css/Video.css");

const VideoAttendeeEditModal = ({ show, onHide, attendee, departments, onSave }) => {
    const [loading, setLoading] = useState(false);

    const isEditing = !!attendee?.id;

    const validationSchema = Yup.object({
        name: Yup.string()
            .required('Nome é obrigatório')
            .min(2, 'Nome deve ter pelo menos 2 caracteres'),
        email: Yup.string()
            .email('Email inválido')
            .required('Email é obrigatório'),
        password: isEditing
            ? Yup.string().min(6, 'Senha deve ter pelo menos 6 caracteres')
            : Yup.string().required('Senha é obrigatória').min(6, 'Senha deve ter pelo menos 6 caracteres'),
        department_ids: Yup.array()
            .min(1, 'Selecione pelo menos um departamento')
            .required('Selecione pelo menos um departamento')
    });

    const initialValues = {
        name: attendee?.name || '',
        email: attendee?.email || '',
        password: '',
        department_ids: attendee?.departments?.map(d => d.id) || []
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true);

            const payload = {
                name: values.name,
                email: values.email,
                department_ids: values.department_ids
            };

            if (values.password) {
                payload.password = values.password;
            }

            let response;
            if (isEditing) {
                response = await ApiService.send(
                    "put",
                    `${process.env.REACT_APP_URL_API}video/attendees/${attendee.id}`,
                    payload,
                    true
                );
            } else {
                payload.password = values.password;
                response = await ApiService.send(
                    "post",
                    `${process.env.REACT_APP_URL_API}video/attendees`,
                    payload,
                    true
                );
            }

            if (response.code === 200) {
                Swal.fire('Sucesso', isEditing ? 'Atendente atualizado!' : 'Atendente criado!', 'success');
                onSave();
                onHide();
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const departmentOptions = departments.map(d => ({
        value: d.id,
        label: d.name
    }));

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-headset me-2"></i>
                    {isEditing ? 'Editar Atendente' : 'Novo Atendente'}
                </Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                    <Form>
                        <Modal.Body>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Nome *</label>
                                    <Field
                                        name="name"
                                        type="text"
                                        className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                                        placeholder="Nome completo"
                                    />
                                    {errors.name && touched.name && (
                                        <div className="invalid-feedback">{errors.name}</div>
                                    )}
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Email *</label>
                                    <Field
                                        name="email"
                                        type="email"
                                        className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                                        placeholder="email@exemplo.com"
                                    />
                                    {errors.email && touched.email && (
                                        <div className="invalid-feedback">{errors.email}</div>
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">
                                        Senha {isEditing ? '(deixe em branco para manter)' : '*'}
                                    </label>
                                    <Field
                                        name="password"
                                        type="password"
                                        className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                                        placeholder={isEditing ? "Nova senha (opcional)" : "Senha"}
                                    />
                                    {errors.password && touched.password && (
                                        <div className="invalid-feedback">{errors.password}</div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Departamentos *</label>
                                <Select
                                    isMulti
                                    options={departmentOptions}
                                    value={departmentOptions.filter(opt => values.department_ids.includes(opt.value))}
                                    onChange={(selected) => {
                                        setFieldValue('department_ids', selected ? selected.map(s => s.value) : []);
                                    }}
                                    placeholder="Selecione os departamentos"
                                    noOptionsMessage={() => "Nenhum departamento disponível"}
                                    className={errors.department_ids && touched.department_ids ? 'is-invalid' : ''}
                                />
                                {errors.department_ids && touched.department_ids && (
                                    <div className="text-danger small mt-1">{errors.department_ids}</div>
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="btn btn-secondary" onClick={onHide}>
                                <i className="fa fa-times me-2"></i>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-success" disabled={isSubmitting || loading}>
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                    <i className="fa fa-check me-2"></i>
                                )}
                                {isEditing ? 'Atualizar' : 'Criar'}
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default VideoAttendeeEditModal;
